/**
 * document-links-api.ts — rezerwacja identyfikatora dokumentu Symfonii pod obieg akceptacji.
 *
 * Dokument Handlu nie ma u nas wiersza, a obieg celuje w rekord wskazany UUID-em. Zanim więc
 * wyślemy dokument do akceptacji, prosimy backend o identyfikator. Powtórzenie zwraca ten sam —
 * dlatego wolno to wołać przy każdym kliknięciu, bez pilnowania stanu po stronie przeglądarki.
 *
 * **Ścieżki BEZ przedrostka `/api`** — klient axios ma go w `baseURL` (`lib/api.ts`). Wpisany
 * drugi raz daje `/api/api/…`, czyli 404, a zapytanie w błędzie chowa całą sekcję akceptacji
 * BEZ ŻADNEGO komunikatu na ekranie. Pilnuje tego kolokowany test.
 */

import { api } from '@/lib/api';
import type { InstanceStatus } from '@/lib/processes/approvals-api';

export type HmLinkKind = 'sale' | 'warehouse' | 'order';

export interface HmDocumentLinkRef {
  /** NASZ identyfikator — to on trafia do wniosku akceptacyjnego jako rekord docelowy. */
  id: string;
  kind: HmLinkKind;
  /** `Id` dokumentu w Symfonii. */
  externalId: string;
  documentNumber: string;
}

export interface HmDocumentLinkDetail extends HmDocumentLinkRef {
  issueDate: string | null;
  contractorName: string | null;
  grossValue: string | null;
  currencyCode: string;
  /** Firma, do której należy powiązanie — potrzebna do zbudowania adresu formatki. */
  companyId: string;
  source: { webapiUrl: string; companyName: string | null };
}

/**
 * Czy dokument jest już powiązany — czysty ODCZYT, bez zakładania czegokolwiek.
 *
 * Formatka pyta o to przy wejściu. Gdyby zamiast tego wołała rezerwację, każde otwarcie
 * dokumentu zostawiałoby wiersz w bazie.
 */
export async function lookupDocumentLink(input: {
  kind: HmLinkKind;
  externalId: number | string;
  companyId: string;
}): Promise<HmDocumentLinkRef | null> {
  const params = new URLSearchParams({
    kind: input.kind,
    externalId: String(input.externalId),
    companyId: input.companyId,
  });
  const res = await api.get<{ link: HmDocumentLinkRef | null }>(
    `/handel-magazyn/document-links?${params.toString()}`,
  );
  return res.data.link;
}

/** Zarezerwuj (albo odzyskaj) identyfikator dla dokumentu o podanym numerze w Symfonii. */
export async function reserveDocumentLink(input: {
  kind: HmLinkKind;
  externalId: number | string;
  companyId: string;
}): Promise<HmDocumentLinkRef> {
  const res = await api.post<HmDocumentLinkRef>(
    `/handel-magazyn/document-links?companyId=${encodeURIComponent(input.companyId)}`,
    { kind: input.kind, externalId: String(input.externalId) },
  );
  return res.data;
}

/**
 * Rozwiąż nasz identyfikator na dokument Symfonii (nawigacja z kokpitu decyzji).
 *
 * Odmowa 409 znaczy, że połączenie wskazuje dziś INNĄ bazę Symfonii niż ta, w której dokument
 * powiązano — wtedy nie ma czego pokazać, bo dokument o tym numerze w bieżącej bazie jest
 * zupełnie innym dokumentem.
 */
export async function fetchDocumentLink(id: string): Promise<HmDocumentLinkDetail> {
  // Bez parametru firmy — wiersz powiązania sam wie, do której należy. Kokpit decyzji zna
  // wyłącznie identyfikator wniosku i nie miałby skąd wziąć firmy.
  const res = await api.get<HmDocumentLinkDetail>(`/handel-magazyn/document-links/${id}`);
  return res.data;
}

/** Wynik rezerwacji dla JEDNEGO dokumentu z paczki. */
export interface HmBatchLinkResult {
  externalId: string;
  linkId?: string;
  documentNumber?: string;
  /** Powód, dla którego tego dokumentu nie udało się powiązać (reszta paczki przechodzi). */
  error?: string;
}

/**
 * Zbiorcza rezerwacja identyfikatorów — jedno żądanie na całą paczkę, jedna sesja Symfonii.
 *
 * Osobne żądanie per dokument wyczerpałoby limit sesji WebAPI (domyślnie 10) już przy
 * kilkunastu zaznaczonych wierszach.
 */
export async function reserveDocumentLinksBatch(input: {
  kind: HmLinkKind;
  externalIds: Array<number | string>;
  companyId: string;
}): Promise<HmBatchLinkResult[]> {
  const res = await api.post<{ results: HmBatchLinkResult[] }>(
    `/handel-magazyn/document-links/batch?companyId=${encodeURIComponent(input.companyId)}`,
    { kind: input.kind, externalIds: input.externalIds.map(String) },
  );
  return res.data.results;
}

/** Stan obiegu jednego dokumentu — tyle, ile potrzebuje kolumna na liście. */
export interface HmApprovalStatus {
  externalId: string;
  linkId: string;
  instanceId: string;
  instanceNumber: number;
  status: InstanceStatus;
  /** Nazwa etapu końcowego zakończonej sprawy; `null` gdy sprawa nie jest zakończona. */
  finalNodeLabel: string | null;
}

/**
 * Stany obiegów dla całej kartoteki — pod kolumnę „Akceptacja".
 *
 * Odczyt wyłącznie z naszej bazy, więc nie spowalnia listy czekającej na Symfonię. Zwracamy mapę
 * po identyfikatorze dokumentu w Symfonii, bo tym kluczem dysponuje wiersz listy.
 */
export async function fetchApprovalStatuses(input: {
  kind: HmLinkKind;
  companyId: string;
}): Promise<Map<string, HmApprovalStatus>> {
  const params = new URLSearchParams({ kind: input.kind, companyId: input.companyId });
  const res = await api.get<{ statuses: HmApprovalStatus[] }>(
    `/handel-magazyn/approval-statuses?${params.toString()}`,
  );
  return new Map(res.data.statuses.map((s) => [s.externalId, s]));
}

/** Adres formatki dokumentu w panelu — jedno miejsce, w którym rodzaj mapuje się na kartotekę. */
const LIST_SEGMENT: Record<HmLinkKind, string> = {
  sale: 'sprzedaz',
  warehouse: 'magazyn',
  order: 'zamowienia',
};

export function documentLinkHref(link: {
  kind: HmLinkKind;
  externalId: string;
  companyId?: string;
}): string {
  const base = `/handel-magazyn/${LIST_SEGMENT[link.kind]}/${link.externalId}`;
  return link.companyId ? `${base}?companyId=${encodeURIComponent(link.companyId)}` : base;
}
